import { Button } from 'components/ui/button'
import { LoaderMain } from 'components/loader'
import InfoBanner from 'components/InfoBanner'
import SectionCard from 'components/CustomCheckout/SectionCard'
import { useNotificationBar } from 'context/notificationContext'
import {
  usePaymentFailureTemplates,
  useUpdatePaymentFailureTemplates,
} from 'hooks/custom-checkout'
import { QueryKeys } from 'hooks/_query_keys'
import { useUploadFiles } from 'hooks/upload'
import { Upload, X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { getErrorResponse, getImageSrc } from 'services/util'

type PaymentFailureCaseKey =
  | 'user_side'
  | 'couldnt_process'
  | 'our_side'
  | 'cancelled'

type PaymentFailureTemplateFormItem = {
  id: string
  caseKey: PaymentFailureCaseKey
  errorCodes: string[]
  image: string
  title: string
  description: string
  ctaText: string
}

type PaymentFailureTemplateFormValues = {
  templates: PaymentFailureTemplateFormItem[]
}

const CASE_LABELS: Record<PaymentFailureCaseKey, string> = {
  user_side: 'User-side',
  couldnt_process: "Couldn't process",
  our_side: 'Our side',
  cancelled: 'Cancelled',
}

const CASE_DESCRIPTIONS: Record<PaymentFailureCaseKey, string> = {
  user_side: 'Customer, UPI PIN, balance, limit, account-state, or bank decline failures.',
  couldnt_process: 'Bank, PG, network, UPI app, NPCI, generic, empty, or unknown failures.',
  our_side: 'Merchant configuration, request, checksum, crypto, and platform-side failures.',
  cancelled: 'User cancelled, request declined, expired, timed out, or incomplete payments.',
}

const CASE_ORDER: Record<PaymentFailureCaseKey, number> = {
  user_side: 1,
  couldnt_process: 2,
  our_side: 3,
  cancelled: 4,
}

const defaultValues: PaymentFailureTemplateFormValues = {
  templates: [],
}

const extractTemplates = (response: any): PaymentFailureTemplateFormItem[] => {
  const body = response?.data?.data ?? response?.data ?? response
  const templates = Array.isArray(body) ? body : body?.templates ?? []

  return templates
    .map((template: any) => ({
      id: String(template?.id ?? ''),
      caseKey: template?.caseKey,
      errorCodes: Array.isArray(template?.errorCodes) ? template.errorCodes : [],
      image: template?.image ?? '',
      title: template?.title ?? '',
      description: template?.description ?? '',
      ctaText: template?.ctaText ?? '',
    }))
    .filter((template: PaymentFailureTemplateFormItem) => Boolean(CASE_LABELS[template.caseKey]))
    .sort((current: PaymentFailureTemplateFormItem, next: PaymentFailureTemplateFormItem) => {
      return CASE_ORDER[current.caseKey] - CASE_ORDER[next.caseKey]
    })
}

const toRelativeAssetPath = (assetPath: string): string => {
  if (!assetPath) return ''

  if (!assetPath.startsWith('http')) {
    return assetPath.replace(/^\/+/, '')
  }

  try {
    return new URL(assetPath).pathname.replace(/^\/+/, '')
  } catch {
    return assetPath
  }
}

const getUploadKey = (response: any): string => {
  const result = response?.data?.data ?? response?.data ?? response

  return toRelativeAssetPath(result?.key ?? result?.url ?? '')
}

const PaymentErrorConfig = () => {
  const notification = useNotificationBar()
  const queryClient = useQueryClient()
  const [uploadingTemplateId, setUploadingTemplateId] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<PaymentFailureTemplateFormValues>({
    mode: 'onChange',
    defaultValues,
  })

  const {
    data: templateResponse,
    isLoading,
    isError,
    error,
  } = usePaymentFailureTemplates()
  const { mutate: updateTemplates, isLoading: isUpdating } =
    useUpdatePaymentFailureTemplates()
  const { mutate: uploadFile } = useUploadFiles()

  useEffect(() => {
    if (!templateResponse || isError) return

    const formValues = { templates: extractTemplates(templateResponse) }
    reset(formValues)
  }, [templateResponse, isError, reset])

  useEffect(() => {
    if (!isError) return
    notification.push(getErrorResponse(error))
  }, [isError, error, notification])

  const templates = watch('templates') ?? []
  const isSaving = isUpdating || Boolean(uploadingTemplateId)

  const handleImageUpload = useCallback(
    (templateId: string, index: number, file?: File) => {
      if (!file) return

      const formData = new FormData()
      formData.append('file', file)
      formData.append('customPath', 'payment-failure-template')

      setUploadingTemplateId(templateId)
      uploadFile(
        { data: formData },
        {
          onSuccess: (response: any) => {
            const imagePath = getUploadKey(response)

            if (!imagePath) {
              notification.push({
                type: 'error',
                title: 'Upload failed',
                message: 'Uploaded image path was not returned by the server.',
              })
              return
            }

            setValue(`templates.${index}.image`, imagePath, {
              shouldDirty: true,
              shouldValidate: true,
            })
          },
          onError: (uploadError: any) => {
            notification.push(getErrorResponse(uploadError))
          },
          onSettled: () => {
            setUploadingTemplateId(null)
          },
        }
      )
    },
    [notification, setValue, uploadFile]
  )

  const onSave = handleSubmit((data) => {
    const payload = {
      // Error-code mappings are intentionally read-only; save only CMS-managed content.
      templates: data.templates.map((template) => ({
        id: template.id,
        caseKey: template.caseKey,
        image: toRelativeAssetPath(template.image),
        title: template.title,
        description: template.description,
        ctaText: template.ctaText,
      })),
    }

    updateTemplates(
      { data: payload },
      {
        onSuccess: (response: any) => {
          const formValues = { templates: extractTemplates(response) }

          reset(formValues)
          queryClient.invalidateQueries([
            QueryKeys.CUSTOM_CHECKOUT,
            'payment-failure-templates',
          ])

          notification.push({
            type: 'success',
            title: 'Saved',
            message: 'Payment error templates saved successfully.',
          })
        },
        onError: (saveError: any) => {
          notification.push(getErrorResponse(saveError))
        },
      }
    )
  })

  if (isLoading) {
    return <LoaderMain size="md" text="Loading payment error templates..." fullscreen />
  }

  return (
    <div className="w-full bg-gray-50 px-6 py-6 dark:bg-slate-900">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          Payment Error Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage checkout failure screen copy and images for fixed payment error buckets.
        </p>
      </div>

      <InfoBanner message="Error codes are read-only. Only image, title, description, and CTA text can be edited." />

      <form className="space-y-5" onSubmit={(event) => event.preventDefault()}>
        {templates.map((template, index) => (
          <SectionCard
            key={template.id}
            title={CASE_LABELS[template.caseKey]}
            description={CASE_DESCRIPTIONS[template.caseKey]}
            badge={`Case ${CASE_ORDER[template.caseKey]}`}
            defaultOpen={index === 0}
          >
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Image
                  </label>

                  <div className="rounded-lg border border-dashed border-blue-300 bg-white p-4 dark:border-blue-700 dark:bg-slate-900">
                    {template.image ? (
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center">
                          <img
                            src={getImageSrc(template.image)}
                            alt={`${CASE_LABELS[template.caseKey]} preview`}
                            className="h-16 w-16 shrink-0 rounded-md border border-gray-200 object-contain dark:border-slate-700"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setValue(`templates.${index}.image`, '', {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }
                          className="rounded-full bg-black/70 p-1.5 text-white hover:bg-black"
                          aria-label="Remove image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer flex-col items-center justify-center rounded-md px-4 py-8 text-center hover:bg-blue-50/50 dark:hover:bg-slate-800">
                        <Upload className="mb-2 h-5 w-5 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          Upload image
                        </span>
                        <span className="mt-1 text-xs text-gray-500">
                          PNG, JPG, SVG, GIF, WEBP
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/gif,image/webp"
                          disabled={isSaving}
                          onChange={(event) => {
                            handleImageUpload(
                              template.id,
                              index,
                              event.target.files?.[0]
                            )
                            event.target.value = ''
                          }}
                        />
                      </label>
                    )}

                    {uploadingTemplateId === template.id && (
                      <p className="mt-2 text-xs text-blue-600">Uploading image...</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Title <span className="text-red-700">*</span>
                  </label>
                  <input
                    className="h-11 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="Enter failure title"
                    maxLength={255}
                    {...register(`templates.${index}.title`, {
                      required: 'Title is required',
                    })}
                  />
                  {errors.templates?.[index]?.title?.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.templates[index]?.title?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    CTA Text <span className="text-red-700">*</span>
                  </label>
                  <input
                    className="h-11 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="Enter CTA text"
                    maxLength={100}
                    {...register(`templates.${index}.ctaText`, {
                      required: 'CTA text is required',
                    })}
                  />
                  {errors.templates?.[index]?.ctaText?.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.templates[index]?.ctaText?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col md:col-span-2">
                  <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description <span className="text-red-700">*</span>
                  </label>
                  <textarea
                    className="min-h-[104px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="Enter failure description"
                    {...register(`templates.${index}.description`, {
                      required: 'Description is required',
                    })}
                  />
                  {errors.templates?.[index]?.description?.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.templates[index]?.description?.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Error Codes
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Read-only mapping stored in CMS for visibility.
                  </p>
                </div>

                <div className="max-h-[280px] overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {template.errorCodes.map((code) => (
                      <span
                        key={`${template.caseKey}-${code}`}
                        className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        ))}

        <div className="sticky bottom-0 z-10 flex justify-end gap-3 border-t border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <Button
            type="button"
            color="blue"
            onClick={onSave}
            disabled={!isDirty || isSaving}
            isLoading={isSaving}
            loadinText="Saving..."
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  )
}

export default PaymentErrorConfig
